import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { FormattedMessage } from 'react-intl';

import { Colors } from '@notino/react-styleguide';

import { useNewPdDesignEnabled } from '@containers/ProductDetailContainer/ProductDetail/hooks/useNewPdDesignEnabled';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import { HorizontalChevron } from '@sharedComponents/Icons/HorizontalChevron';

import messages from '../../../../messages';

import {
  IconWrapper,
  InfoWrapper,
  ShowMore,
  ShowMoreWrapper,
  ExpandableDescription,
} from './styled';

interface IExpandableInfoProps {
  info: string;
  trackingAction: string;
  productCode: string;
}

export const ExpandableInfo: React.FC<IExpandableInfoProps> = ({
  info,
  trackingAction,
  productCode,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const { getTimeFromInit } = useTrackingContext();
  const [showMoreButton, setShowMoreButton] = React.useState(false);
  const descriptionRef = React.useRef<HTMLDivElement>();
  const infoWrapperRef = React.useRef<HTMLDivElement>();
  const newPdDesignEnabled = useNewPdDesignEnabled();

  const [inViewRef, inView] = useInView({ triggerOnce: true });

  React.useEffect(() => {
    if (
      inView &&
      infoWrapperRef.current?.offsetHeight <
        descriptionRef.current?.scrollHeight
    ) {
      setShowMoreButton(true);
    }
  }, [inView, setShowMoreButton]);

  const onShowAllClick = React.useCallback(() => {
    const newIsExpanded = !isExpanded;

    dispatchTrackingEvent({
      event: 'element_click',
      element: {
        timing: getTimeFromInit(),
        name: productCode,
        action: trackingAction,
        type: 'product_detail',
        interaction: 'click',
        mode: newIsExpanded ? 'on' : 'off',
      },
      _clear: true,
    });

    setIsExpanded(newIsExpanded);
  }, [isExpanded, getTimeFromInit, productCode, trackingAction]);

  return (
    <>
      <InfoWrapper
        ref={infoWrapperRef}
        isExpanded={isExpanded}
        newPdDesignEnabled={newPdDesignEnabled}
        className={'expandable-info'}
      >
        <ExpandableDescription
          newDesign={newPdDesignEnabled}
          ref={descriptionRef}
          dangerouslySetInnerHTML={{
            __html: info,
          }}
        />
      </InfoWrapper>
      <ShowMoreWrapper ref={inViewRef}>
        <ShowMore showMoreButton={showMoreButton} onClick={onShowAllClick}>
          <FormattedMessage {...messages.showAllText} />
          <IconWrapper>
            <HorizontalChevron
              isOpened={isExpanded}
              color={Colors.basic}
              width="14px"
            />
          </IconWrapper>
        </ShowMore>
      </ShowMoreWrapper>
    </>
  );
};
